import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "@radix-ui/react-slot"

import { cn } from "@/lib/utils"

const iconWrapperVariants = cva(
  "rounded-full",
  {
    variants: {
      variant: {
        default: "",
        destructive: "",
        secondary: "",
        accent: "",
        muted: "",
      },
      background: {
        solid: "",
        outline: "border",
        opaque: "",
        ghost: "",
      },
      size: {
        sm: "h-6 w-6 p-1",
        default: "h-10 w-10 p-2",
        lg: "h-12 w-12 p-3",
      },
    },
    compoundVariants: [
      // default (primary)
      { variant: "default", background: "solid",   className: "bg-primary text-primary-foreground" },
      { variant: "default", background: "outline",  className: "border-primary text-primary" },
      { variant: "default", background: "opaque",   className: "bg-primary/10 text-primary" },
      { variant: "default", background: "ghost",    className: "text-primary" },
      // destructive
      { variant: "destructive", background: "solid",   className: "bg-destructive text-destructive-foreground" },
      { variant: "destructive", background: "outline",  className: "border-destructive text-destructive" },
      { variant: "destructive", background: "opaque",   className: "bg-destructive/10 text-destructive" },
      { variant: "destructive", background: "ghost",    className: "text-destructive" },
      // secondary
      { variant: "secondary", background: "solid",   className: "bg-secondary text-secondary-foreground" },
      { variant: "secondary", background: "outline",  className: "border-secondary text-secondary-foreground" },
      { variant: "secondary", background: "opaque",   className: "bg-secondary/10 text-secondary-foreground" },
      { variant: "secondary", background: "ghost",    className: "text-secondary-foreground" },
      // accent
      { variant: "accent", background: "solid",   className: "bg-orange-500 text-white" },
      { variant: "accent", background: "outline",  className: "border-orange-500 text-orange-500" },
      { variant: "accent", background: "opaque",   className: "bg-orange-500/10 text-orange-500" },
      { variant: "accent", background: "ghost",    className: "text-orange-500" },
      // muted
      { variant: "muted", background: "solid",   className: "bg-muted text-muted-foreground" },
      { variant: "muted", background: "outline",  className: "border-muted-foreground/50 text-muted-foreground" },
      { variant: "muted", background: "opaque",   className: "bg-muted-foreground/10 text-muted-foreground" },
      { variant: "muted", background: "ghost",    className: "text-muted-foreground" },
    ],
    defaultVariants: {
      variant: "default",
      background: "opaque",
      size: "default",
    },
  }
)

type IconWrapperProps = VariantProps<typeof iconWrapperVariants> & {
  className?: string
  children: React.ReactNode
}

export function IconWrapper({
  className,
  children,
  ...props
}: IconWrapperProps) {
  return (
    <Slot
      data-slot="icon-wrapper"
      className={cn(iconWrapperVariants(props), className)}
    >
      {children}
    </Slot>
  )
}
